import { Result } from "effect";
import {
  decodeErrorReply,
  decodeQueueReply,
  decodeReviewReply,
  decodeSetList,
  isConflictCode,
  type ConflictCode,
  type ErrorCode,
  type PauseRequest,
} from "../core/api.ts";
import type { SetSource } from "./source.ts";

export type RpcFailure = "network" | "status" | "json" | "decode";

export interface RpcErrorInit {
  method: string;
  path: string;
  kind: RpcFailure;
  status: number | null;
  code?: ErrorCode | null;
  requestId?: string | null;
  message: string;
  hint: string;
  cause?: unknown;
}

export class RpcError extends Error {
  override readonly name = "RpcError";
  readonly method: string;
  readonly path: string;
  readonly kind: RpcFailure;
  readonly status: number | null;
  readonly code: ErrorCode | null;
  readonly requestId: string | null;
  readonly hint: string;

  constructor(init: RpcErrorInit) {
    super(init.message, { cause: init.cause });
    this.method = init.method;
    this.path = init.path;
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code ?? null;
    this.requestId = init.requestId ?? null;
    this.hint = init.hint;
  }
}

export const TIMEOUT_MS = 10_000;

export const HINTS = {
  network: "Check that the frontend process is up with overmind status, then retry.",
  unreachable: "Start the backend with overmind start or bun run backend, then retry.",
  stub: "Retry once this endpoint is built in src/server/index.ts.",
  log: "Check logs/backend.log, then retry.",
};

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function hintFor(status: number, requestId: string | null): string {
  if (status === 502) return HINTS.unreachable;
  if (status === 501) return HINTS.stub;
  return requestId ? `Check logs/${today()}.log for request ${requestId}, then retry.` : HINTS.log;
}

function where(method: string, path: string): string {
  return `${method} ${path.split("?")[0]}`;
}

export function networkError(method: string, path: string, cause?: unknown): RpcError {
  const timedOut = cause instanceof DOMException && cause.name === "TimeoutError";
  const message = timedOut
    ? `${where(method, path)} got no answer within ${TIMEOUT_MS / 1000} s.`
    : `${where(method, path)} got no answer from the app server.`;
  return new RpcError({ method, path, kind: "network", status: null, message, hint: HINTS.network, cause });
}

export interface Envelope {
  readonly code?: ErrorCode | null;
  readonly requestId?: string | null;
}

export function statusError(
  method: string,
  path: string,
  status: number,
  reason: string | null = null,
  { code = null, requestId = null }: Envelope = {},
): RpcError {
  const message = `${where(method, path)} answered ${status}${reason ? `: ${reason}` : ""}.`;
  return new RpcError({ method, path, kind: "status", status, code, requestId, message, hint: hintFor(status, requestId) });
}

export function jsonError(method: string, path: string, status: number): RpcError {
  const message = `${where(method, path)} answered ${status} with a reply that is not JSON.`;
  return new RpcError({ method, path, kind: "json", status, message, hint: HINTS.log });
}

export function decodeError(method: string, path: string, status: number, issue: string): RpcError {
  const message = `${where(method, path)} answered ${status} with a reply of the wrong shape: ${issue}`;
  return new RpcError({ method, path, kind: "decode", status, message, hint: HINTS.log });
}

export function conflictCode(error: unknown): ConflictCode | null {
  return error instanceof RpcError && error.status === 409 && error.code !== null && isConflictCode(error.code)
    ? error.code
    : null;
}

function failure(method: string, path: string, status: number, body: unknown, parsed: boolean): RpcError {
  if (!parsed) return statusError(method, path, status);
  const envelope = decodeErrorReply(body);
  if (Result.isSuccess(envelope)) {
    const { error, code, requestId } = envelope.success;
    return statusError(method, path, status, error || null, { code, requestId });
  }
  const reason = body && typeof body === "object" && "error" in body && typeof body.error === "string" ? body.error : null;
  return statusError(method, path, status, reason || null);
}

export async function rpc<T>(
  path: string,
  decode: (body: unknown) => Result.Result<T, string>,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!headers.has("accept")) headers.set("accept", "application/json");

  let text: string;
  let res: Response;
  try {
    res = await fetch(path, { ...init, method, headers, signal: AbortSignal.timeout(TIMEOUT_MS) });
    text = await res.text();
  } catch (cause) {
    throw networkError(method, path, cause);
  }

  let body: unknown;
  let parsed = true;
  try {
    body = JSON.parse(text);
  } catch {
    parsed = false;
  }

  if (!res.ok) throw failure(method, path, res.status, body, parsed);
  if (!parsed) throw jsonError(method, path, res.status);
  const decoded = decode(body);
  if (Result.isFailure(decoded)) throw decodeError(method, path, res.status, decoded.failure);
  return decoded.success;
}

export const rpcSource: SetSource = {
  sets: () => rpc("/api/sets", decodeSetList),
  queue: (setId) => rpc(`/api/sets/${setId}/queue`, decodeQueueReply),
  review: (cardId, request) =>
    rpc(`/api/cards/${cardId}/reviews`, decodeReviewReply, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
    }),
  pauseHanziPrompts: (paused) =>
    rpc("/api/settings/hanzi-prompts", decodeSetList, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ paused } satisfies PauseRequest),
    }),
};
